import {Separator} from 'platform/components';
import {Heading, Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {isNotNil, isPositive} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useBpmAmountFormatter} from '../utils/useBpmAmountFormatter';
import {BpmEstimateRow} from './BpmEstimateRow';

interface BpmBreakdownProps extends TestIdProps {
  estimate: number;
  grossBpm: number;
  depreciationPercent: number;
  subtitle: string;
  footnote: string;
  isFixedRate?: boolean;
  /** Advertised price incl. VAT in euros; without it no total is shown. */
  adPrice?: number | null;
}

/**
 * The amount and its breakdown. Shared by the single estimate and by the
 * selected scenario below 3 000 km, so both read the same way.
 */
export function BpmBreakdown(props: BpmBreakdownProps) {
  const formatAmount = useBpmAmountFormatter();
  const formatNumber = useFormatNumber();

  const adPrice = props.adPrice;
  // Show does not narrow, so the amounts are formatted before it.
  const adPriceLabel = isNotNil(adPrice) ? formatAmount(adPrice) : '';
  const totalLabel = isNotNil(adPrice) ? formatAmount(adPrice + props.estimate) : '';

  return (
    <VStack spacing={3}>
      <VStack>
        <Heading size={2} data-testid={suffixTestId('estimate', props)}>
          {formatAmount(props.estimate)}
        </Heading>
        <Text size="xSmall" color="tertiary">
          {props.subtitle}
        </Text>
      </VStack>

      {/* Without depreciation the gross equals the result and the rows would just repeat it. */}
      <Show when={isPositive(props.depreciationPercent)}>
        <VStack spacing={2}>
          <Separator spacing={0} />
          <BpmEstimateRow
            label={
              props.isFixedRate
                ? i18n.t('entity.sourcing.bpm.grossBpmFixedRate')
                : i18n.t('entity.sourcing.bpm.grossBpm')
            }
            value={formatAmount(props.grossBpm)}
            data-testid={suffixTestId('grossBpm', props)}
          />
          <BpmEstimateRow
            label={i18n.t('entity.sourcing.bpm.depreciationLabel')}
            value={`− ${formatNumber(props.depreciationPercent, 0)} %`}
            data-testid={suffixTestId('depreciation', props)}
          />
        </VStack>
      </Show>

      {/* Only shown when the ad itself is priced in euros - see getAdPriceInEur. */}
      <Show when={isNotNil(adPrice)}>
        <VStack spacing={2}>
          <Separator spacing={0} />
          <BpmEstimateRow
            label={i18n.t('entity.sourcing.vehiclePreview.labels.priceWithVat')}
            value={adPriceLabel}
            data-testid={suffixTestId('adPrice', props)}
          />
          <BpmEstimateRow
            label={i18n.t('general.labels.total')}
            value={totalLabel}
            isEmphasized
            data-testid={suffixTestId('total', props)}
          />
        </VStack>
      </Show>

      <Text size="xSmall" color="secondary">
        {props.footnote}
      </Text>
    </VStack>
  );
}
